import React, { useEffect, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import TwygsEditorNodes from '../nodes/TwygsEditorNodes';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import getTheme from '../themes/TwygsEditorTheme';
import { TRANSFORMERS } from '@lexical/markdown'; // Use Markdown transformers if enabled

import './index.css';
import { $convertFromMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
const preprocessMarkdown = (markdown) => {
  return markdown
    .replace(/\r/g, '') // Remove carriage returns
    .replace(/(\*\*.*?\*\*)(\s*#)/g, '$1\n\n$2') // Ensure blank line before header following bold text
    .replace(/([^\n])\n(#)/g, '$1\n\n$2') // Ensure blank line before headers
    .replace(/([^\n])\n(-|\*)/g, '$1\n\n$2') // Ensure blank line before lists
    .split('\n') // Split into lines
    .map((line) => line.trim()) // Trim spaces around each line
    .join('\n'); // Rejoin lines with a single newline
};

const UpdatePlugin = ({ markdown }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (markdown) {
      editor.update(() => {
        $convertFromMarkdownString(markdown, TRANSFORMERS);
      });
    }
  }, [markdown, editor]);

  return null;
};

const LexicalViewerTwygsBot = ({ rawMarkdown }) => {
  const editorStateRef = useRef(null);
  const validMarkdown = preprocessMarkdown(rawMarkdown);
  const initialConfig = {
    editorState: () => $convertFromMarkdownString(validMarkdown, TRANSFORMERS),
    namespace: 'Twygs-Viewer',
    editable: false,
    nodes: [...TwygsEditorNodes],
    onError: (error) => {
      console.error('Lexical Viewer Error:', error);
    },
    theme: {
      ...getTheme(false),
      paragraph: 'leading-relaxed',
      text: {
        ...getTheme(false).text,
        base: 'text-gray-800'
      }
    }
  };

  return (
    <>
      <LexicalComposer initialConfig={initialConfig as any}>
        <div className="editor-shell">
          <div className="editor-container">
            <div className="editor-inner twygs-bot-viewer">
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    className="editor-input viewer-input"
                    style={{
                      outline: 'none',
                      cursor: 'default',
                      userSelect: 'text'
                    }}
                  />
                }
                placeholder={null}
                ErrorBoundary={LexicalErrorBoundary}
              />
              <OnChangePlugin
                onChange={(editorState) => {
                  editorStateRef.current = editorState;
                }}
              />
              <ListPlugin />
              <LinkPlugin />
              <ClickableLinkPlugin disabled={false} newTab />
              <TabIndentationPlugin />
              <HistoryPlugin />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
              <UpdatePlugin markdown={validMarkdown} />
            </div>
          </div>
        </div>
      </LexicalComposer>
    </>
  );
};

export default LexicalViewerTwygsBot;
